import React, { useEffect, useState } from 'react';
import { DATA_CONFIGURATION, DEFAULT_VALUE, ENTER, MAX_CHAR_COUNT } from '../ISRConstants';
import { TConnector, TField, TFieldDataConfiguration, TProductCategory, TSubmodel } from '../Pages/DefineModel/model';
import Card from 'Components/DesignSystem/Card/Card';
import TextField from 'Components/DesignSystem/TextField/TextField';

type TextConfigurationProps = {
    id: string;
    selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
    updateFieldDataConfiguration: (updatedConfig: TFieldDataConfiguration) => void;
}

/**
 * The runtime engine loads `defaultValue` into the field's `value`, so `''` arrives as `value: [""]` and
 * counts as filled — it satisfies page completion and auto-advances the preview (AB#15115).
 * An empty or whitespace-only default is therefore sent as `null`, NEVER `''`; any other text is kept as typed.
 */
export const toApiDefaultValue = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;
    const str = String(value);
    return str.trim() === '' ? null : str;
};

const TextConfiguration = ({id, selectedItem, updateFieldDataConfiguration} : TextConfigurationProps) => {
    const [fieldDataConfiguration, setFieldDataConfiguration] = useState<TFieldDataConfiguration>({});
    // What the input shows. Kept apart from the saved `defaultValue` so typing a leading space is not wiped by the `null` normalisation.
    const [defaultValueText, setDefaultValueText] = useState('');

    useEffect(() => {
        const existingValue = selectedItem && 'dataConfiguration' in selectedItem && selectedItem?.dataConfiguration ? selectedItem.dataConfiguration : {}
        // A record saved before AB#15115 can carry `defaultValue: ''`; normalise it so the next autosave repairs it.
        const defaultValue = toApiDefaultValue(existingValue.defaultValue);
        setFieldDataConfiguration('defaultValue' in existingValue ? { ...existingValue, defaultValue } : existingValue);
        setDefaultValueText(defaultValue ?? '');
    }, [selectedItem]);

    useEffect(() => {
        const hasChanges = Object.keys(fieldDataConfiguration).some(
            (key) => fieldDataConfiguration[key as keyof TFieldDataConfiguration] !== undefined
        );
        if (hasChanges) {
            updateFieldDataConfiguration(fieldDataConfiguration);
        }
    }, [fieldDataConfiguration]);


    const handleMaxCharacterCountChange = (value: number) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, maxCharacterCount: Math.max(0, value)}));
    };

    const handleIncrementChange = (value: string) => {
        setDefaultValueText(value);
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, defaultValue: toApiDefaultValue(value)}));
    };

    return (
        <Card id={id} data-testid={id} title={DATA_CONFIGURATION}>
            <div id={`${id}-main-config`} className='tw-flex tw-items-start tw-gap-5'>
                <TextField
                    id={`${id}-char-count-input`}
                    data-testid={`${id}-char-count-input`}
                    label={MAX_CHAR_COUNT}
                    type='number'
                    minValue={0}
                    onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                    value={String(fieldDataConfiguration.maxCharacterCount || '')}
                    onChange={(value) => handleMaxCharacterCountChange(Number(value))}
                    placeholder={ENTER}
                    // min-w-max keeps the label on one line — wrapped, it pushed this input below the Default Value input.
                    className='tw-w-[20%] tw-min-w-max'
                />
                <TextField
                    id={`${id}-default-value-input`}
                    data-testid={`${id}-default-value-input`}
                    label={DEFAULT_VALUE}
                    value={defaultValueText}
                    onChange={handleIncrementChange}
                    placeholder={ENTER}
                    className='tw-w-[36%]'
                />
            </div>
        </Card>
    );
}

export default TextConfiguration;